// ── Le collecteur du support : ce qui s'est passe juste avant une demande ──
// Des tampons circulaires en memoire : les erreurs de la page, les requetes /api/* en echec, le fil des actions.
// Jamais d'en-tete, de corps envoye, de cookie, de jeton ni de requete (?…) : le chemin, la methode, le statut, et le
// champ « error » d'une reponse JSON.

export interface CollectedError { at: string; message: string; source?: string }
export interface CollectedRequest { at: string; method: string; path: string; status: number; message?: string }
export interface TrailEntry { at: string; label: string }
export interface Collected { errors: CollectedError[]; requests: CollectedRequest[]; trail: TrailEntry[] }

/** Ce que le collecteur touche de la fenetre (simule dans les tests) */
export interface CollectorTarget {
  location: { origin: string };
  addEventListener(type: string, listener: (event: Event) => void): void;
  fetch: typeof fetch;
}

const MAX = { errors: 20, requests: 20, trail: 30 } as const;
const ERROR_READ_MS = 300;
const cut = (s: string, n: number) => (s.length > n ? s.slice(0, n) : s);
const now = () => new Date().toISOString();
const push = <T,>(list: T[], item: T, max: number) => { list.push(item); if (list.length > max) list.splice(0, list.length - max); };

type ErrorLike = { message?: unknown; filename?: string; lineno?: number; colno?: number; error?: unknown; reason?: unknown };

export function createCollector() {
  const errors: CollectedError[] = [];
  const requests: CollectedRequest[] = [];
  const trailList: TrailEntry[] = [];
  let installed = false;

  const recordError = (message: string, source?: string) =>
    push(errors, { at: now(), message: cut(message, 2000), ...(source ? { source: cut(source, 2000) } : {}) }, MAX.errors);

  /** Le chemin d'un appel /api/* de ce site, sans sa requete ; null pour tout autre appel */
  const apiPath = (target: CollectorTarget, input: RequestInfo | URL): string | null => {
    try {
      const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const url = new URL(raw, target.location.origin);
      return url.origin === target.location.origin && url.pathname.startsWith('/api/') ? cut(url.pathname, 500) : null;
    } catch {
      return null;
    }
  };

  /** Le champ error d'une reponse JSON, lu sur une copie : la reponse rendue reste intacte */
  const errorField = async (res: Response): Promise<string | undefined> => {
    if (!res.headers.get('content-type')?.includes('json')) return undefined;
    const read = res.clone().json().then(
      (body: { error?: unknown }) => (typeof body?.error === 'string' ? cut(body.error, 300) : undefined),
      () => undefined,
    );
    // Une reponse qui tarde ne retient pas l'appelant : au-dela, l'echec est garde sans son message
    return Promise.race([read, new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), ERROR_READ_MS))]);
  };

  function install(target: CollectorTarget) {
    if (installed) return;
    installed = true;
    target.addEventListener('error', event => {
      const e = event as unknown as ErrorLike;
      const where = e.filename ? `${e.filename}:${e.lineno ?? 0}` : undefined;
      const stack = e.error instanceof Error && e.error.stack ? cut(e.error.stack, 1000) : undefined;
      recordError(String(e.message ?? e.error ?? 'Erreur'), [where, stack].filter(Boolean).join('\n') || undefined);
    });
    target.addEventListener('unhandledrejection', event => {
      const reason = (event as unknown as ErrorLike).reason;
      const message = reason instanceof Error ? reason.message : String(reason);
      recordError(message, reason instanceof Error && reason.stack ? cut(reason.stack, 1000) : undefined);
    });

    const original = target.fetch.bind(target);
    target.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
      const path = apiPath(target, input);
      const method = cut((init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET')).toUpperCase(), 10);
      try {
        const res = await original(input, init);
        if (path && res.status >= 400) {
          const message = await errorField(res);
          push(requests, { at: now(), method, path, status: res.status, ...(message ? { message } : {}) }, MAX.requests);
        }
        return res;
      } catch (err) {
        if (path) push(requests, { at: now(), method, path, status: 0, message: cut(err instanceof Error ? err.message : String(err), 300) }, MAX.requests);
        throw err;
      }
    }) as typeof fetch;
  }

  /** Une action de l'editeur ; la meme deux fois de suite (un glisser) ne compte qu'une fois */
  function trail(label: string) {
    const last = trailList.at(-1);
    if (last?.label === label) { last.at = now(); return; }
    push(trailList, { at: now(), label: cut(label, 200) }, MAX.trail);
  }

  const read = (): Collected => ({ errors: [...errors], requests: [...requests], trail: trailList.map(t => ({ ...t })) });

  return { install, read, trail };
}

/** Le collecteur de la page, installe par SupportProvider */
export const collector = createCollector();
